import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { trackedMap, trackedSet } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismMessage,
  PrismModal,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralTablesCellsTdTwoLine from 'lumen/components/general/tables/cells/td-two-line';
import AddSubscriptionAccess from 'lumen/components/settings/users/modals/add-subscription-access';
import BulkActionRow from 'lumen/components/settings/users/tables/bulk-action-row';
import CellDelete from 'lumen/components/settings/users/tables/cells/cell-delete';
import CellProductAccess from 'lumen/components/settings/users/tables/cells/cell-product-access';
import CellRole from 'lumen/components/settings/users/tables/cells/cell-role';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type { BulkDeletePayload } from 'lumen/services/settings-user-subscription-access';
import {
  ACCESS_RIGHTS,
  type AccessRight,
  MIXED,
  type MixedRole,
} from 'lumen/utils/constants/users-constants';

import styles from './modal-subscription-access.module.css';

interface SubscriptionAccessChange {
  accessRight?: string;
  accessibleProducts?: Record<string, boolean>;
  subscriptionIds: number[];
  userId: string;
}

interface SortState {
  isAscending: boolean;
  valuePath: string;
}

interface ModalSubscriptionAccessSignature {
  Args: {
    isLoading: boolean;
    isLoadingMore: boolean;
    isOwnUser: boolean;
    onAddAccess: (subscriptionIds: number[]) => Promise<void>;
    onBulkDelete: (payload: BulkDeletePayload) => Promise<boolean>;
    onClose: () => void;
    onDelete: (
      subscriptionId: number,
      subscriptionName: string,
    ) => Promise<void>;
    onLoadMore: () => void;
    onSave: (changes: SubscriptionAccessChange[]) => Promise<boolean>;
    onSearchChange: (event: Event) => void;
    onSortChange: (sorts: SortState[]) => void;
    relationships: Userhotelrelationship[];
    searchText: string;
    sorts: SortState[];
    userEmail: string;
    userId: string;
  };
}

interface SubscriptionAccessRow extends CustomEmberTableRow {
  biBenchmarking: string;
  createdAt: string;
  disableCheckboxSelection: boolean;
  hasBiBenchmarkingAccess: boolean;
  hasRateInsightAccess: boolean;
  isDisabled: boolean;
  isLocked: boolean;
  onDelete?: () => void;
  onProductChange?: (valuePath: string, checked: boolean) => void;
  onRoleChange?: (role: AccessRight) => void;
  originalRole: AccessRight;
  rateAndMarketInsight: string;
  role: AccessRight;
  subscriptionId: number;
  subscriptionName: { primary: string; secondary: string };
}

interface RowOverride {
  biBenchmarking?: boolean;
  rateAndMarketInsight?: boolean;
  role?: AccessRight;
}

const COLUMNS = {
  BI_BENCHMARKING: 'biBenchmarking',
  CREATED_AT: 'createdAt',
  DELETE: 'delete',
  SUBSCRIPTION_NAME: 'subscriptionName',
  RATE_AND_MARKET_INSIGHT: 'rateAndMarketInsight',
  ROLE: 'role',
} as const;

function toProductAccessState(
  hasAccess: boolean,
  isNotEditable: boolean,
): string {
  if (isNotEditable) {
    return hasAccess ? 'readonly' : 'unavailable';
  }
  return hasAccess ? 'checked' : 'unchecked';
}

export type { SubscriptionAccessChange };

export default class ModalSubscriptionAccess extends Component<ModalSubscriptionAccessSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];

  @tracked private bulkBiBenchmarkingOverride: boolean | undefined;
  @tracked private bulkRateAndMarketInsightOverride: boolean | undefined;
  @tracked private bulkRoleOverride: AccessRight | undefined;
  @tracked private isSaving = false;
  // Selection is stored as subscriptionIds rather than row objects. PrismTable/
  // ember-table match selection to rows by object identity, but the `rows`
  // getter rebuilds row objects whenever it recomputes (e.g. after clearing
  // overrides on select). Deriving `rowSelection` from the same `rows` getter
  // guarantees the selected objects are reference-equal to the rendered rows.
  private rowOverrides = trackedMap<number, RowOverride>();
  private selectedIds = trackedSet<number>();

  get allChanges(): SubscriptionAccessChange[] {
    const changes = new Map<number, SubscriptionAccessChange>();

    // Inline per-row edits: each row carries its own override values.
    for (const [subscriptionId, override] of this.rowOverrides) {
      const relationship = this.args.relationships.find(
        (relationship) => relationship.subscriptionId === subscriptionId,
      );
      if (!relationship) {
        continue;
      }

      const change = this.buildChange(relationship, {
        role: override.role,
        rateAndMarketInsight: override.rateAndMarketInsight,
        biBenchmarking: override.biBenchmarking,
      });

      if (change) {
        changes.set(subscriptionId, change);
      }
    }

    // Bulk edits: every selected row gets the same bulk override values.
    // Mutually exclusive with inline edits (selecting clears `rowOverrides`),
    // so the `changes.has` guard is defensive only.
    for (const row of this.rowSelection) {
      if (changes.has(row.subscriptionId)) {
        continue;
      }

      const relationship = this.args.relationships.find(
        (relationship) => relationship.subscriptionId === row.subscriptionId,
      );
      if (!relationship) {
        continue;
      }

      const change = this.buildChange(relationship, {
        role: this.bulkRoleOverride,
        rateAndMarketInsight: this.bulkRateAndMarketInsightOverride,
        biBenchmarking: this.bulkBiBenchmarkingOverride,
      });

      if (change) {
        changes.set(row.subscriptionId, change);
      }
    }

    return [...changes.values()];
  }

  get bulkBiBenchmarking(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.BI_BENCHMARKING);
  }

  get bulkRateAndMarketInsight(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.RATE_AND_MARKET_INSIGHT);
  }

  get bulkRole(): { isIndeterminate: boolean; value: AccessRight | MixedRole } {
    if (this.bulkRoleOverride !== undefined) {
      return { isIndeterminate: false, value: this.bulkRoleOverride };
    }

    const roles = this.rowSelection.map((row) => row.role);
    const uniqueRoles = [...new Set(roles)];
    const firstRole = uniqueRoles[0];

    if (uniqueRoles.length === 1 && firstRole) {
      return { isIndeterminate: false, value: firstRole };
    }

    return { isIndeterminate: true, value: MIXED };
  }

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('useraccess_rights_header_dateadded'),
        valuePath: COLUMNS.CREATED_AT,
        textAlign: 'left' as const,
        width: 140,
        isSortable: true,
      },
      {
        name: this.intl.t('settings.users.column.subscription'),
        valuePath: COLUMNS.SUBSCRIPTION_NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        isSortable: true,
        width: 400,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRole,
        textAlign: 'left' as const,
        width: 200,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.rate_insight_market_insight'),
        valuePath: COLUMNS.RATE_AND_MARKET_INSIGHT,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
        width: 200,
      },
      {
        name: this.intl.t('settings.users.column.bi_benchmarking'),
        valuePath: COLUMNS.BI_BENCHMARKING,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: '',
        valuePath: COLUMNS.DELETE,
        tdComponent: CellDelete,
        textAlign: 'right' as const,
        isSortable: false,
        width: 50,
      },
    ];
  }

  private get hasBulkOverrides(): boolean {
    return (
      this.hasSelection &&
      (this.bulkRoleOverride !== undefined ||
        this.bulkRateAndMarketInsightOverride !== undefined ||
        this.bulkBiBenchmarkingOverride !== undefined)
    );
  }

  get hasPendingChanges(): boolean {
    return this.rowOverrides.size > 0 || this.hasBulkOverrides;
  }

  get hasSelection(): boolean {
    return this.selectedIds.size > 0;
  }

  get isSaveDisabled(): boolean {
    return this.isSaving || !this.hasPendingChanges;
  }

  // `@cached` so a single render pass sees one stable set of row objects.
  // `rowSelection` is derived from this same getter, so the selected objects
  // are reference-equal to the rendered rows (ember-table matches selection by
  // identity). The cache invalidates when `rowOverrides`/relationships change,
  // keeping cell values reactive.
  @cached
  get rows(): SubscriptionAccessRow[] {
    // Read lazily inside the row's `isDisabled` getter so selection changes
    // stay reactive without aliasing `this`.
    const hasSelection = (): boolean => this.hasSelection;

    return this.args.relationships.map((relationship) => {
      const {
        accessRight,
        createdAt,
        isLocked,
        name,
        products,
        subscriptionDescription,
        subscriptionId,
      } = relationship;
      const override = this.rowOverrides.get(subscriptionId);

      const originalRole = accessRight.toLowerCase() as AccessRight;
      const effectiveRole = override?.role ?? originalRole;
      const isAdmin = effectiveRole === ACCESS_RIGHTS.ADMIN;

      const rateInsightProduct = products?.rate_insight;
      const biAccessProduct = products?.revenue_insight;
      const hasRateInsight = rateInsightProduct !== undefined;
      const hasBiAccess = biAccessProduct !== undefined;

      const originalRateInsight = rateInsightProduct?.is_accessible ?? false;
      const originalBiAccess = biAccessProduct?.is_accessible ?? false;

      const effectiveRateInsight = isAdmin
        ? true
        : (override?.rateAndMarketInsight ?? originalRateInsight);
      const effectiveBiAccess = isAdmin
        ? true
        : (override?.biBenchmarking ?? originalBiAccess);

      const rateInsightEditable = rateInsightProduct?.is_editable ?? false;
      const biAccessEditable = biAccessProduct?.is_editable ?? false;

      // A product checkbox locks when it's missing, admin-forced, not editable,
      // or it's the last accessible product (the other product is present but
      // off) so access can't be toggled down to zero. `otherIsLastAccess` guards
      // the lock so a single standalone product never locks itself.
      const isNotEditable = (
        present: boolean,
        editable: boolean,
        checked: boolean,
        otherPresent: boolean,
        otherChecked: boolean,
      ): boolean =>
        !present ||
        isAdmin ||
        !editable ||
        (checked && otherPresent && !otherChecked);

      const isRateInsightNotEditable = isNotEditable(
        hasRateInsight,
        rateInsightEditable,
        effectiveRateInsight,
        hasBiAccess,
        effectiveBiAccess,
      );
      const isBiAccessNotEditable = isNotEditable(
        hasBiAccess,
        biAccessEditable,
        effectiveBiAccess,
        hasRateInsight,
        effectiveRateInsight,
      );

      return {
        subscriptionId,
        isLocked,
        hasRateInsightAccess: originalRateInsight,
        hasBiBenchmarkingAccess: originalBiAccess,
        originalRole,
        [COLUMNS.CREATED_AT]: createdAt
          ? dayjs(createdAt).format('DD MMM YYYY')
          : this.intl.t('general.missing'),
        [COLUMNS.SUBSCRIPTION_NAME]: {
          primary: name,
          secondary: subscriptionDescription,
        },
        [COLUMNS.ROLE]: effectiveRole,
        [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
          effectiveRateInsight,
          isLocked || isRateInsightNotEditable,
        ),
        [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
          effectiveBiAccess,
          isLocked || isBiAccessNotEditable,
        ),
        onDelete: (): void => {
          void this.args.onDelete(subscriptionId, name);
        },
        originalValues: {
          [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
            originalRateInsight,
            isLocked || isRateInsightNotEditable,
          ),
          [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
            originalBiAccess,
            isLocked || isBiAccessNotEditable,
          ),
        },
        disableCheckboxSelection: isLocked,
        get isDisabled(): boolean {
          return isLocked || hasSelection();
        },
        onRoleChange: (role: AccessRight): void => {
          if (this.hasSelection) {
            return;
          }
          this.updateRowOverride(subscriptionId, { role }, relationship);
        },
        onProductChange: (valuePath: string, checked: boolean): void => {
          if (this.hasSelection) {
            return;
          }
          const key =
            valuePath === COLUMNS.RATE_AND_MARKET_INSIGHT
              ? 'rateAndMarketInsight'
              : 'biBenchmarking';
          this.updateRowOverride(
            subscriptionId,
            { [key]: checked },
            relationship,
          );
        },
      };
    });
  }

  // Derived from the cached `rows` so the selected objects are reference-equal
  // to the rendered rows (ember-table matches selection by object identity).
  get rowSelection(): SubscriptionAccessRow[] {
    return this.rows.filter((row) => this.selectedIds.has(row.subscriptionId));
  }

  private buildChange(
    relationship: Userhotelrelationship,
    override: RowOverride,
  ): SubscriptionAccessChange | undefined {
    const originalRole = relationship.accessRight.toLowerCase() as AccessRight;
    const effectiveRole = override.role ?? originalRole;
    const isAdmin = effectiveRole === ACCESS_RIGHTS.ADMIN;

    const base = {
      subscriptionIds: [relationship.subscriptionId],
      userId: this.args.userId,
    };

    let roleChanged = false;
    const change: SubscriptionAccessChange = { ...base };

    if (override.role && override.role !== originalRole) {
      change.accessRight = override.role.toLowerCase();
      roleChanged = true;
    }

    const originalRateInsight =
      relationship.products?.rate_insight?.is_accessible ?? false;
    const originalBiAccess =
      relationship.products?.revenue_insight?.is_accessible ?? false;

    const newRateInsight = isAdmin
      ? true
      : (override.rateAndMarketInsight ?? originalRateInsight);
    const newBiAccess = isAdmin
      ? true
      : (override.biBenchmarking ?? originalBiAccess);

    const productsChanged =
      newRateInsight !== originalRateInsight ||
      newBiAccess !== originalBiAccess;

    if (productsChanged) {
      change.accessibleProducts = {
        rate_insight: newRateInsight,
        revenue_insight: newBiAccess,
      };
    }

    return roleChanged || productsChanged ? change : undefined;
  }

  private computeBulkProductState(
    column:
      | typeof COLUMNS.RATE_AND_MARKET_INSIGHT
      | typeof COLUMNS.BI_BENCHMARKING,
  ): { checked: boolean; indeterminate: boolean } {
    if (this.rowSelection.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const bulkOverride =
      column === COLUMNS.RATE_AND_MARKET_INSIGHT
        ? this.bulkRateAndMarketInsightOverride
        : this.bulkBiBenchmarkingOverride;

    if (bulkOverride !== undefined) {
      return { checked: bulkOverride, indeterminate: false };
    }

    const states = this.rowSelection.map((row) => row[column]);
    const isAccessible = (state: string): boolean =>
      state === 'checked' || state === 'readonly';
    const allChecked = states.every(isAccessible);
    const allUnchecked = states.every((state) => !isAccessible(state));

    if (allChecked) {
      return { checked: true, indeterminate: false };
    }
    if (allUnchecked) {
      return { checked: false, indeterminate: false };
    }

    return { checked: false, indeterminate: true };
  }

  private resetBulkState(): void {
    this.rowOverrides.clear();
    this.selectedIds.clear();
    this.bulkRoleOverride = undefined;
    this.bulkRateAndMarketInsightOverride = undefined;
    this.bulkBiBenchmarkingOverride = undefined;
  }

  private selectedSubscriptionIds(): number[] {
    return [...this.selectedIds];
  }

  private updateRowOverride(
    subscriptionId: number,
    update: Partial<RowOverride>,
    relationship: Userhotelrelationship,
  ): void {
    const existing = this.rowOverrides.get(subscriptionId);
    const override = { ...existing, ...update };

    const originalRole = relationship.accessRight.toLowerCase() as AccessRight;

    if (override.role === originalRole) {
      delete override.role;
    }

    const originalRateInsight =
      relationship.products?.rate_insight?.is_accessible ?? false;
    const originalBiAccess =
      relationship.products?.revenue_insight?.is_accessible ?? false;

    if (override.rateAndMarketInsight === originalRateInsight) {
      delete override.rateAndMarketInsight;
    }
    if (override.biBenchmarking === originalBiAccess) {
      delete override.biBenchmarking;
    }

    const effectiveRole = override.role ?? originalRole;
    if (effectiveRole === ACCESS_RIGHTS.ADMIN) {
      delete override.rateAndMarketInsight;
      delete override.biBenchmarking;
    }

    if (Object.keys(override).length > 0) {
      this.rowOverrides.set(subscriptionId, override);
    } else {
      this.rowOverrides.delete(subscriptionId);
    }
  }

  @action
  async bulkDelete(): Promise<void> {
    if (this.rowSelection.length === 0) {
      return;
    }

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.bulk.delete_confirm_title'),
      text: this.intl.t('settings.users.bulk.delete_confirm_text', {
        count: this.rowSelection.length,
      }),
    });

    if (!confirmWasClicked) {
      return;
    }

    const payload: BulkDeletePayload = {
      subscription_ids: this.selectedSubscriptionIds(),
      user_id: this.args.userId,
      exclude: false,
    };

    this.isSaving = true;
    try {
      const success = await this.args.onBulkDelete(payload);

      if (success) {
        this.resetBulkState();
      }
    } finally {
      this.isSaving = false;
    }
  }

  @action
  async cancelAll(): Promise<void> {
    if (this.hasPendingChanges) {
      const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.users.bulk.cancel_confirm_text'),
      });

      if (!confirmWasClicked) {
        return;
      }
    }

    this.resetBulkState();
    this.args.onClose();
  }

  @action
  handleBulkBiBenchmarkingToggle(): void {
    this.bulkBiBenchmarkingOverride = !this.bulkBiBenchmarking.checked;
  }

  @action
  handleBulkRateAndMarketInsightToggle(): void {
    this.bulkRateAndMarketInsightOverride =
      !this.bulkRateAndMarketInsight.checked;
  }

  @action
  handleBulkRoleChange(option: Option): void {
    this.bulkRoleOverride = option.key as AccessRight;
  }

  @action
  handleRowSelect(
    rowSelection: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    if (this.isSaving) {
      return;
    }

    this.bulkRoleOverride = undefined;
    this.bulkRateAndMarketInsightOverride = undefined;
    this.bulkBiBenchmarkingOverride = undefined;

    const selectedRows = Array.isArray(rowSelection)
      ? rowSelection
      : [rowSelection];

    // Inline per-row edits are mutually exclusive with bulk select: drop them
    // on any selection change so the table reverts to original values. Because
    // `rowOverrides` is tracked, clearing it rebuilds `rows` (and the derived
    // `rowSelection`) reactively, so the cells reflect the reset immediately.
    this.rowOverrides.clear();

    this.selectedIds.clear();
    for (const row of selectedRows) {
      this.selectedIds.add(
        (row as unknown as SubscriptionAccessRow).subscriptionId,
      );
    }
  }

  @action
  async saveAll(): Promise<void> {
    if (!this.hasPendingChanges) {
      return;
    }

    this.isSaving = true;

    try {
      const success = await this.args.onSave(this.allChanges);

      if (success) {
        this.resetBulkState();
      }
    } finally {
      this.isSaving = false;
    }
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      @title={{@userEmail}}
      data-test-id="modal-subscription-access"
    >
      <:default>
        <div class="flex items-center justify-between px-400 py-300">
          <AddSubscriptionAccess @onAdd={{@onAddAccess}} @userId={{@userId}} />
          <div class="flex items-center gap-200">
            <PrismInput
              @iconAfter={{hash name="search"}}
              @onInput={{@onSearchChange}}
              @placeholder={{t "settings.users.filter.search_placeholder"}}
              @removePadding={{true}}
              @value={{@searchText}}
              data-test-id="modal-subscription-access-search"
            />
          </div>
        </div>

        <div class={{styles.table-container}}>
          <PrismTable
            @checkboxSelectionMode="multiple"
            @columns={{this.columns}}
            @containerWidthAdjustment={{0}}
            @isLoading={{@isLoading}}
            @isLoadingMore={{@isLoadingMore}}
            @lastReached={{@onLoadMore}}
            @onRowSelect={{this.handleRowSelect}}
            @onUpdateSorts={{@onSortChange}}
            @oneCellBulkEditRow={{false}}
            @rowSelection={{this.rowSelection}}
            @rows={{this.rows}}
            @sorts={{@sorts}}
            @widthConstraint="gte-container-slack"
          >
            <:bulkEditRow as |bulkEditRow|>
              <BulkActionRow
                @badgeColspan={{2}}
                @bulkBiBenchmarking={{this.bulkBiBenchmarking}}
                @bulkRateAndMarketInsight={{this.bulkRateAndMarketInsight}}
                @bulkRole={{this.bulkRole}}
                @deleteColspan={{1}}
                @numberOfSelectedRows={{bulkEditRow.numberOfSelectedRows}}
                @onBulkBiBenchmarkingToggle={{this.handleBulkBiBenchmarkingToggle}}
                @onBulkDelete={{this.bulkDelete}}
                @onBulkRateAndMarketInsightToggle={{this.handleBulkRateAndMarketInsightToggle}}
                @onBulkRoleChange={{this.handleBulkRoleChange}}
              />
            </:bulkEditRow>
            <:emptyState>
              <div class="flex h-full items-center justify-center p-600">
                <PrismMessage
                  @illustration="100x100_search"
                  @title={{t "settings.users.empty.nothing_matches_filters"}}
                />
              </div>
            </:emptyState>
          </PrismTable>
        </div>
      </:default>

      <:footer>
        <div class="flex h-full items-center justify-end gap-200 px-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{if this.hasPendingChanges this.cancelAll @onClose}}
            data-test-id="modal-subscription-access-cancel"
          />
          <PrismButton
            @buttonType="primary"
            @isDisabled={{this.isSaveDisabled}}
            @isLoading={{this.isSaving}}
            @label={{t "general.save"}}
            @onClick={{this.saveAll}}
            data-test-id="modal-subscription-access-save"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
